'use client';

import React, { useId } from 'react';
import twMerge from 'utilities/src/twMerge';
import type { TocItem } from 'utils/postBodyUtils';

type Props = {
  items: TocItem[];
  activeId: string | null;
  onSelect: (id: string) => void;
  className?: string;
};

/**
 * "In this article": jump links to the article's H2s, with the current section highlighted.
 */
export default function ArticleToc({ items, activeId, onSelect, className }: Props) {
  const labelId = useId();

  if (!items.length) return null;

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    const heading = document.getElementById(id);
    if (!heading) return;

    event.preventDefault();
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    heading.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    // Move focus with the jump so keyboard and screen reader users continue from the section
    heading.setAttribute('tabindex', '-1');
    heading.focus({ preventScroll: true });
    window.history.replaceState(null, '', `#${id}`);
    onSelect(id);
  };

  return (
    <nav aria-labelledby={labelId} className={twMerge('flex flex-col gap-4', className)}>
      <p id={labelId} className="site-overline-s text-site-neutrals-600">
        In this article
      </p>
      <ul className="flex flex-col">
        {items.map((item) => {
          const isActive = item.id === activeId;
          return (
            <li key={item.id}>
              <a
                href={`#${item.id}`}
                aria-current={isActive ? 'location' : undefined}
                onClick={(event) => handleClick(event, item.id)}
                className={twMerge(
                  'site-paragraph-s border-site-neutrals-100 text-site-neutrals-600 hover:text-site-neutrals-900 block border-l py-2.5 pl-3 font-medium transition-colors',
                  isActive && 'border-site-primary-600 text-site-neutrals-900 border-l-2 pl-[0.6875rem] font-bold',
                )}
              >
                {item.label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
